import {Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  ActionType,
  AppComponentName,
  DeviceInfo,
  Event,
  EventAction,
  EventIgnore,
  EventLog,
  EventLogInfo,
  EventTooltipHistory,
  EventType,
  LightFloodSchedule,
  Message,
  MessageActionType,
  MessageNoteCategory,
  MessageType,
  Sensor,
  SensorGroup,
  SensorReadingType,
  UserAccessRole,
  UserAccessType,
  UserGroup,
  WaterSchedule,
  WaterScheduleInfo
} from '@models';
import { Task } from '../../shared/models/Device/Task.model';
import {DeviceService} from '@services/device.service';
import moment from 'moment';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {ConfirmationService} from 'primeng/api';
import {Table} from 'primeng/table';
import {SensorService} from '@services/sensor.service';
import {SensorGroupService} from '@services/sensorgroup.service';
import {UserService} from '@services/user.service';
import {AppComponent} from '../../app.component';
import {TriggerService} from './trigger.service';
import {EventMapper} from './trigger.mapping';
import {ActionResult, ConditionResult, TaskResults} from '@models/Event/TaskResult.model';
import {AppMessageService} from '@services/message.service';
import {ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {debounce} from 'lodash-es';
import {HubConnectionState} from '@microsoft/signalr';
import {EMPTY, from} from 'rxjs';
import {Observable} from 'rxjs';
import {HubConnectionFactory} from '@services/hub-connection-factory.service';
import {TimerRound} from '@models/Irrigation/timerRound';
import {
  EditEventConditionThresholdComponent,
  EditEventConditionThresholdParams
} from './edit-event-condition-threshold/edit-event-condition-threshold.component';
import {
  EditEventsAndActionsModalComponent,
  EditEventsAndActionsModalParams
} from './edit-events-and-actions-modal/edit-events-and-actions-modal.component';
import {
  EvaluateTimerEventResultsModalComponent,
  EvaluateTimerEventResultsModalParams
} from './evaluate-timer-event-results-modal/evaluate-timer-event-results-modal.component';
import {LogsModalComponent, LogsModalParams} from './logs-modal/logs-modal.component';
import {
  InvokeResultsModalComponent,
  InvokeResultsModalParams
} from './invoke-results-modal/invoke-results-modal.component';
import {
  SensorConditionsEvalModalComponent,
  SensorConditionsEvalModalParams
} from './sensor-conditions-eval-modal/sensor-conditions-eval-modal.component';
import Timeout = NodeJS.Timeout;
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from '../messages/changelog-notes/changelognotes.components';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { ActionQueue, ActionQueueRules } from '@models/ActionQueue/ActionQueue.model';
import { ActionQueueService } from '@services/actionQueue.service';
import { ViewActionQueueComponent, ViewActionQueueParams } from '../trigger-queues/view-action-queues/view-action-queues.component';
import { ActionQueueLive } from '@models/ActionQueue/ActionQueueLive.model';
import {
  FloodIrrigationActionEntry,
  ManualFloodIrrigationModalComponent,
  ManualFloodIrrigationModalParams
} from './manual-flood-irrigation-modal/manual-flood-irrigation-modal.component';
import { fill } from 'lodash';

const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');

@Component({
  standalone: false,
  selector: 'app-trigger-settings',
  templateUrl: './trigger-settings.component.html',
  styleUrls: [ './trigger-settings.component.scss' ],
})

export class TriggerSettingsComponent implements OnInit, OnDestroy {

  @ViewChild('triggerTable') table: Table;
  @ViewChild(LogsModalComponent) logsModalComponent!: LogsModalComponent;

  public auth = new Authorization();
  public common = new CommonShared();
  
  modalDragOn: boolean = false;

  public searchRecallTimer?: Timeout;
  public eventSearchText = '';
  public eventUpdateDisplayTimer?: Timeout;
  public events: Event[] = [];
  public eventsDisplayed: Event[] = [];
  public eventsCheckedCount: number = 0;
  public screenInnerWidth: number;

  private event: Event = new Event(); // {} as Event;
  private eventMapper = new EventMapper();
  private taskResults: TaskResults;
  private actionResults: ActionResult[] = [];
  private conditionResults: ConditionResult[] = [];
  private userGroups: UserGroup[] = [];
  private associatedUserGroups: UserGroup[] = [];
  private eventTooltipHistory: EventTooltipHistory[] = [];

  private sensorDataTypes: SensorReadingType[] = [];
  private sensorGroups: SensorGroup[] = [];
  private actionQueues: ActionQueue[] = [];
  private groupActionQueueSetToValue: ActionQueue;
  public actionQueueGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Action Queue',
    noResultsFound: 'No Action Queues found!',
    searchOnKey: 'name'
  };

  private username = 'unknown';
  private selectedUserGroup: UserGroup;
  private evaluateTimes: string[] = [];
  private devices: DeviceInfo[] = [];
  private IrrDestinationDDOptions: string[];
  private CamGatewayDDOptions: string[];
  private IrrZoneDDOptions: string[];

  private sensorBasedTab = false;
  private timerBasedTab = false;
  private modelBasedTab = false;
  private motionBasedTab = false;
  private preEditObjectAsJSONString = '';
  private changeLogNotes = '';
  private globalWaterSchedules: WaterSchedule[] = [];

  private readonly globalSwitch: boolean;

  // {field: 'currentValue', header: 'Eval', width: '6%'},

  public eventTableCols = [
    { field: 'checkbox', header: 'CheckBox', width: '50px', resizable: false, defaultWidth: '50px' },
    { field: 'name', header: 'Name', width: '200px', resizable: true, defaultWidth: '200px' },
    { field: 'currentValue', header: 'Eval', width: '100px', resizable: true, defaultWidth: '100px' },
    {field: 'conditionCount', header: 'Condition', width: '310px', resizable: true, defaultWidth: '310px' },
    {field: 'actionCount', header: 'Actions', width: '135px', resizable: true, defaultWidth: '135px' },
    {field: 'owner', header: 'Owner', width: '175px', resizable: true, defaultWidth: '175px' },
    {field: 'modifiedDate', header: 'Modified', width: '195px', resizable: true, defaultWidth: '195px' },
    {field: 'isDisabled', header: 'Enabled', width: '100px', resizable: false, defaultWidth: '100px' },
    {field: 'actionQueue', header: 'Queue', width: '60px', resizable: false, defaultWidth: '60px' },
    {field: 'triggerNow', header: 'Trigger', width: '65px', resizable: false, defaultWidth: '65px' },
    {field: 'miniChart', header: 'Actions (Last 24 hrs)', width: '150px', resizable: false, defaultWidth: '150px' },
    {field: 'actionLog', header: 'Logs', width: '65px', resizable: false, defaultWidth: '65px' },
    {field: 'copy', header: 'Copy', width: '65px', resizable: false, defaultWidth: '65px' },
    {field: 'edit', header: 'Edit', width: '65px', resizable: false, defaultWidth: '65px' },
    {field: 'delete', header: 'Del', width: '65px', resizable: false, defaultWidth: '65px' }
  ];

  public selectedColumns = this.eventTableCols.slice();

  public columnDropDownSettings: any = {
    singleSelection: false,
    idField: 'field',
    textField: 'header',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 1,
    allowSearchFilter: false,
    defaultOpen: false,
    text: 'Columns'
  };


  public eventsLoaded = true;
  public associatedUserGroupsLoaded = true;
  public userGroupsLoaded = true;
  public sensorGroupsLoaded = false;

  logConfig = {
    size: 'lg',
    backdrop: false,
    ignoreBackdropClick: false
  };

  config = {
    backdrop: false,
    ignoreBackdropClick: true
  };

  evaluateInvokeConfig = {
    backdrop: false,
    ignoreBackdropClick: false
  };

  public scanModalRef: BsModalRef;
  public scannedSensor: string;
  public availableDevices: MediaDeviceInfo[];
  public selectedDevice: MediaDeviceInfo;
  public groupChangeModalRef: BsModalRef;
  public groupChangeAction: string = '';
  public groupThresholdChangeByValue: number = 0;
  public groupThresholdSetToValue: number = 0;
  public groupPauseValue: number
  public updatedEventToDisplay = '';
  private hubConnection: signalR.HubConnection;


  constructor(private toastr: ToastrService, private modalService: BsModalService, private confirmationService: ConfirmationService, 
              private deviceService: DeviceService, public global: AppComponent, public _userService: UserService, private _actionQueueService: ActionQueueService,
              private _triggerService: TriggerService, private _sensorService: SensorService, private hubFactory: HubConnectionFactory,
              private _sensorGroupService: SensorGroupService, public _appMessageService: AppMessageService, private route: ActivatedRoute,
              private location: Location) {
    this.globalSwitch = global.globalSwitch;
    this.onResize = debounce(this.onResize, 150, {leading: false, trailing: true});

    this.hubConnection = this.hubFactory.create('/api/hubs/eventaction');
    this.hubConnection.onclose(() => console.log('hubConnection.onclose') );
    this.hubConnection.onreconnecting(() => console.log('Attempting reconnect...') );
    this.hubConnection.onreconnected(() => console.log('Reconnected.') );
    this.hubConnection.on('refreshEvent', (updateType: string, updatedEvent: string) => this.refreshEvent(updateType, updatedEvent));
  }


  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.screenInnerWidth = window.innerWidth;
  }


  ngOnInit(): void {
    this.screenInnerWidth = window.innerWidth;

    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      () => {
        this.toastr.error('Error unable to read current user\'s info');
      });

    this.loadSelectedColumns()
    this.loadColumnWidths();
  }


  startUp(): void {
    this.getAssociatedUserGroups();
    this.loadEvaluationTimes();
    this.loadIrrigationZones();
    this.startSignalR();
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  private startSignalR(): Observable<any> {
    return (this.hubConnection.state === HubConnectionState.Disconnected) ? from(this.hubConnection.start()) : EMPTY;
  }

  public ngOnDestroy(): void {
    this.stopSignalR();
  }

  private stopSignalR() {
    this.hubConnection.stop();
  }

  private refreshEvent(updateType: string, updatedEventInfo: string) {
    switch (updateType.toLowerCase()) {
    case 'edit':
      this.refreshEventOnEdit(updatedEventInfo);
      break;
    case 'new':
      this.refreshEventOnNew(updatedEventInfo);
      break;
    case 'del':
      this.refreshEventOnDel(updatedEventInfo);
      break;
    case 'editignore':
      this.refreshEventIgnore(updatedEventInfo);
      break;
    default:
      break;
    }
  }


  public refreshEventOnEdit(updatedEvent: string): void {
    this._triggerService.clearCachedVariables();

    this.eventMapper.devices = this.devices;
    const eventInfo = this.eventMapper.parseUCaseEventInfoObj2LCaseEventInfo(updatedEvent);
    const event: Event = this.eventMapper.mapEventInfo2Event(eventInfo);
    this.auth.mapUserAccessForEvent(event, this.globalSwitch, this.username, this.userGroups);
    this.updateEventActionSchedules(event);
    this.assignActionQueuePositionToEvent(event);

    event.sensorConditions.forEach(s => {
      s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID);
      s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id);
    });

    const matchedEventIndex = this.events.findIndex(e => e.id === event.id);
    if (matchedEventIndex < 0) {
      return;
    }

    this.events[matchedEventIndex] = event;
    this.events = [...this.events];

    if (this.eventsDisplayed.map(x => x.id).includes(event.id)) {
      this.searchEventsTextChanged();
    }

    if (event.userFeatureAccess['ViewListing']) {
      this.updatedEventToDisplay = moment().local().format('hh:mm A') + ' -> \'' + event.name + '\' was edited';
      this.startTimer4EventUpdateDisplay();
      this.toastr.info(event.name + ' was edited');
    }
  }


  public refreshEventIgnore(updatedEventInfo: string): void {
    var eventIds: number[] = JSON.parse(updatedEventInfo);

    eventIds.forEach(eventId => {
      var event = this.events.find(x => x.id == eventId);

      if (event != undefined) {
        this.updatedEventToDisplay = moment().local().format('hh:mm A') + ' -> \'' + event.name + '\' pause state edited';
        this.startTimer4EventUpdateDisplay();
        this.toastr.info(event.name + ' was edited');
      }
    });

    this.mapTooltipsToEventItems();
  }


  public refreshEventOnNew(updatedEvent: string): void {
    this._triggerService.clearCachedVariables();

    this.eventMapper.devices = this.devices;
    const eventInfo = this.eventMapper.parseUCaseEventInfoObj2LCaseEventInfo(updatedEvent);
    const event: Event = this.eventMapper.mapEventInfo2Event(eventInfo);
    this.auth.mapUserAccessForEvent(event, this.globalSwitch, this.username, this.userGroups);
    this.updateEventActionSchedules(event);
    this.assignActionQueuePositionToEvent(event);

    event.sensorConditions.forEach(s => {
      s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID);
      s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id);
    });

    this.events = [...this.events, event];
    this.searchEventsTextChanged();

    if (event.userFeatureAccess['ViewListing']) {
      this.updatedEventToDisplay = moment().local().format('hh:mm A') + ' -> \'' + event.name + '\' was created';
      this.startTimer4EventUpdateDisplay();
      this.toastr.info(event.name + ' was created');
    }
  }


  public refreshEventOnDel(updatedEventID: string): void {
    this._triggerService.clearCachedVariables();
    
    const eventId: number = parseInt(updatedEventID, 10);
    const eventIndex = this.events.findIndex(e => e.id === eventId);
    if (eventIndex > -1) {
      const eventName = this.events[eventIndex].name;
      if (this.events[eventIndex].userFeatureAccess['ViewListing']) {
        this.updatedEventToDisplay = moment().local().format('hh:mm A') + ' -> \'' + eventName + '\' was deleted';
        this.startTimer4EventUpdateDisplay();
        this.toastr.info(eventName + ' was deleted');
      }
      this.events.splice(eventIndex, 1);
      this.events = [...this.events];

      if (this.eventsDisplayed.map(x => x.id).includes(eventId)) {
        this.searchEventsTextChanged();
      }
    }
  }

  
  loadEvaluationTimes(): void {
    this.evaluateTimes = [];
    for (let hour = 0; hour < 24; hour++) {
      this.evaluateTimes.push ( zeroPad(hour,2) + ':15' );
      this.evaluateTimes.push ( zeroPad(hour,2) + ':45' );
    }
  }


  loadIrrigationZones(): void {
    this.IrrZoneDDOptions = [];
    for (let i = 0; i < 30; i++) {
      this.IrrZoneDDOptions = [... this.IrrZoneDDOptions, i.toString()];
    }
  }


  getGatewayDevices(): void {
    this.IrrDestinationDDOptions = [];
    this.CamGatewayDDOptions = [];

    this.deviceService.getDevices().subscribe(
      results => {
        if (results != null) {
          this.devices = results;
          this.devices.forEach(d => {
            if (d.userName === this.username || this.associatedUserGroups.findIndex(u => u.id === d.userGroupId) > -1) {
              if (d.serialNumber !== '') {
                this.IrrDestinationDDOptions.push(d.name);
                this.CamGatewayDDOptions.push(d.name);
              }
            }
          });
          this.getEvents();
        }
        else {
          this.toastr.warning('No gateway devices found');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while retrieving gateway devices from the server');
      });
  }


  getIrrigationSchedules(): void {
    this.deviceService.getGlobalWaterSchedules().subscribe(
      results => {
        if(results && results.length !== 0){
          this.globalWaterSchedules = this.convertWaterScheduleInfos2WaterSchedules(results);
          this.events.forEach(e => this.updateEventActionSchedules(e) );
        }
      },
      () => {
        this.toastr.error('Error occurred while retrieving irrigation schedules from the server');
      });
  }


  convertWaterScheduleInfos2WaterSchedules(waterScheduleInfos: WaterScheduleInfo[]): WaterSchedule[] {
    const waterSchedules: WaterSchedule[] = [];

    if (waterScheduleInfos == null) {
      return waterSchedules;
    }

    waterScheduleInfos.forEach( wsInfo => {
      let timerRounds: TimerRound[] = [];

      if (wsInfo.timerRounds != null) {
        timerRounds = JSON.parse(wsInfo.timerRounds);
      }

      const waterSchedule: WaterSchedule = {
        id: wsInfo.id,
        name: wsInfo.name,
        isEnabled: wsInfo.isEnabled,
        isSelfOwned: wsInfo.isSelfOwned,
        cycles: 0,
        timeOff: 0,
        timeOn: 0,
        timerRounds,
        usedByCount: 0,
        usedByTrigger: [],
        ownerID: -1,
        ownerName: '',
        ownerChanged: false
      };

      this.calculateWaterScheduleCycleOnOffValues(waterSchedule);
      
      waterSchedules.push(waterSchedule);
    });

    return waterSchedules;
  }


  calculateWaterScheduleCycleOnOffValues(waterSchedule: WaterSchedule) {
    waterSchedule.cycles = 0;
    waterSchedule.timeOn = 0;
    waterSchedule.timeOff = 0;

    waterSchedule.timerRounds.forEach((x)=>{
      waterSchedule.timeOn += x.on * x.multiplier;
      waterSchedule.timeOff += x.off * x.multiplier;
      waterSchedule.cycles += x.multiplier;
    });
  }


  newEvent(): Event {
    this.event.id = 0;
    return {
      id: this.event.id,
      name: '',
      description: '',
      userName: this.username,
      userGroup: '',
      userGroupID: null,
      userAccess: UserAccessType.ReadWrite,
      evaluateDaysOfWeek: [true, true, true, true, true, true, true],
      evaluateStartTime: '00:15',
      evaluateEndTime: '23:45',
      eventType: EventType.SensorBased,
      sensorConditions: [],
      timerConditions: [],
      motionConditions: [],
      modelConditions: [],
      eventActions: [this.newEventAction()],
      isDisabled: false,
      isDisabledColor: 'clear',
      createdDate: moment().local().toDate(),
      modifiedDate: moment().local().toDate(),
      ignoreUntilDate: moment().local().add(-1, 'days').toDate(),
      checked: false,
      overallActionStatus: { state: 0, duration: 0 },
      useActionQueue: false,
      actionQueueId: 0
    };
  }


  newEventAction(): EventAction {
    return {
      id: 0,
      eventID: this.event.id,
      type: ActionType.Email,
      destination: '',
      deviceName: '',
      deviceSerial: '',
      zone: 20,
      scheduleName: '',
      scheduleID: -1,
      taskAction: '',
      onError: '',
      mediaResolution: 'Low',
      mediaDuration: 5,
      mediaDelay: 0,
      cameraId: 0,
      cameraName: '',
      cameraModel: '',
      cameraPresets: '',
      mediaActionOptions: [],
      sourceZone: 21,
      fillZone: 22,
      fillScheduleName: '',
      fillScheduleId: -1,
      drainZone: 23,
      drainScheduleName: '',
      drainScheduleId: -1,
      irrigationActionOptions: undefined,
      notificationSectionOptions: undefined
    };
  }

  setEventType(eventType: string): void {
    this.sensorBasedTab = false;
    this.timerBasedTab = false;
    this.modelBasedTab = false;
    this.motionBasedTab = false;

    if (eventType === 'SensorBased') {
      this.event.eventType = EventType.SensorBased;
      this.sensorBasedTab = true;
    } else if (eventType === 'TimerBased') {
      this.event.eventType = EventType.TimerBased;
      this.timerBasedTab = true;
    } else if (eventType === 'ModelBased') {
      this.event.eventType = EventType.ModelBased;
      this.modelBasedTab = true;
    } else if (eventType === 'MotionBased') {
      this.event.eventType = EventType.MotionBased;
      this.motionBasedTab = true;
    }
  }


  isEvent(eventType: string): boolean {
    if (eventType === 'SensorBased') {
      return true;
    } else if (eventType === 'TimerBased') {
      return true;
    } else if (eventType === 'ModelBased') {
      return true;
    } else if (eventType === 'MotionBased') {
      return true;
    }
    return false;
  }

  getEvents(): void {
    this.eventsLoaded = false;
    this._triggerService.getEvents('Loading Events ...').subscribe(
      results => {
        if (results != null) {
          this.eventMapper.devices = this.devices;
          this.events = this.eventMapper.mapEventInfos2Events(results);
          this.events.forEach(e => {
            this.auth.mapUserAccessForEvent(e, this.globalSwitch, this.username, this.userGroups);
            e.isDisabledColor = this.getEnabledColor(e.isDisabled, e.ignoreUntilDate);
          });

          this.route.params.subscribe(params => {
            if (params['id']) {
              this.loadEventActionWithQRCode(params['id']);
            }
          });

          this.route.url.subscribe(segments => {
            if (segments.length >= 3 && segments[0].path === 'events' && segments[1].path === 'manualflood') {
              const eventId = parseInt(segments[2].path, 10);
              if (!isNaN(eventId)) {
                //this.openManualFloodIrrigationModal(eventId);
                this.loadResoucesForManualFloodIrrigation(eventId);
              }
            }
          });

          this.eventsLoaded = true;
          this.getIrrigationSchedules();

          if (this.sensorGroups.length < 1) {
            this.getSensorGroups();
          }

          this.startTimer4RecallLastSearchFilter();
          this.startTimerEventWindowCheck();
        } else {
          this.toastr.warning('No Events found.');
        }

        this.getActionQueues(false);
        return;
      },
      () => {
        this.toastr.error('Error occurred while retrieving Event list.');
      });
  }


  loadEventActionWithQRCode(param?: string): void {
    if (!param || param === '') {
      return;
    }
    this.global.mobileSwitch = true;
    this.eventSearchText = param;
    this.searchEventsTextChanged();
  }


  // Invoked from the Actions-column "FloodIrrigation" link. Reflect the deep-link
  // route in the address bar (without a full router navigation, which would reload
  // the page) and open the same modal the /events/manualflood/<id> route opens.
  openManualFloodFromLink(eventId: number): void {
    this.location.go('/events/manualflood/' + eventId);
    this.loadResoucesForManualFloodIrrigation(eventId);
  }


  loadResoucesForManualFloodIrrigation(eventId: number) {
    var event = this.events.find(e => e.id === eventId);
    if (!event) {
      this.toastr.warning('Event not found for ID: ' + eventId);
      return;
    }
    
    this.deviceService.getGlobalWaterSchedules().subscribe(
      results => {
        if(results && results.length !== 0){
          this.globalWaterSchedules = this.convertWaterScheduleInfos2WaterSchedules(results);
          this.updateEventActionSchedules(event);
          this.openManualFloodIrrigationModal(eventId);
        }
      },
      () => {
        this.toastr.error('Error occurred while retrieving irrigation schedules from the server');
      });
  }


  openManualFloodIrrigationModal(eventId: number): void {
    const event = this.events.find(e => e.id === eventId);
    if (!event) {
      this.toastr.warning('Event not found for ID: ' + eventId);
      return;
    }
    else {
      this.eventSearchText = event.name;
      this.searchEventsTextChanged();
    }

    const floodActions = event.eventActions.filter(a => a.type === ActionType.FloodIrrigation);
    if (floodActions.length === 0) {
      this.toastr.warning('No flood irrigation action found for event: ' + event.name);
      return;
    }

    const actions: FloodIrrigationActionEntry[] = floodActions.map(floodAction => {
      const fillSchedule = this.globalWaterSchedules.find(x => x.id === floodAction.fillScheduleId);
      const drainSchedule = this.globalWaterSchedules.find(x => x.id === floodAction.drainScheduleId);

      return {
        actionId: floodAction.id,
        fillZone: floodAction.fillZone,
        drainZone: floodAction.drainZone,
        fillTimeSeconds: fillSchedule ? fillSchedule.timeOn : 6 * 60,
        holdTimeSeconds: drainSchedule ? drainSchedule.timeOff : 15 * 60,
        drainTimeSeconds: drainSchedule ? drainSchedule.timeOn : 15 * 60
      };
    });

    const params: ManualFloodIrrigationModalParams = {
      eventName: event.name,
      actions: actions
    };

    const modalRef = this.modalService.show(ManualFloodIrrigationModalComponent, {
      ...this.evaluateInvokeConfig,
      initialState: params
    });

    modalRef.onHide.subscribe(() => {
      const result = modalRef.content?.result;
      if (result && result.executed) {
        const lightFloodSchedules: LightFloodSchedule[] = result.actions.map(action => ({
          actionId: action.actionId,
          fillTimeSeconds: action.fillTimeSeconds,
          holdTimeSeconds: action.holdTimeSeconds,
          drainTimeSeconds: action.drainTimeSeconds
        }));
        this.invokeManualFloodIrrigation(eventId, lightFloodSchedules);
        this.toastr.success('Manual flood irrigation executed for ' + event.name);
      }
    });
  }


  invokeManualFloodIrrigation(eventId: number, lightFloodSchedules: LightFloodSchedule[]): void {
    var event = this.events.find(e => e.id === eventId);
    if (!event) {
      this.toastr.error('Event not found for ID: ' + eventId);
      return;
    }

    this._triggerService.invokeManualFloodIrrigation(eventId, lightFloodSchedules).subscribe(
      results => {
        this.taskResults = results;
        this.createMessageData(MessageActionType.Edited, event, 'Manually Triggered');
        this.actionResults = this.buildDetailsForActionResults(event, this.taskResults.actionResults);
        
        event.overallActionStatus = { state: 1, duration: 0 };
        this.startTimer4RefreshAfterManualTrigger(event);

        if (this.actionResults != null) {
          const params: InvokeResultsModalParams = {
            event,
            actionResults: this.actionResults
          };
          this.modalService.show(InvokeResultsModalComponent, { initialState: params, ...this.evaluateInvokeConfig });
        }
      },
      () => {
        this.toastr.error('Error occurred while executing manual flood irrigation.');
      }
    );
  }


  
  getAssociatedUserGroups(): void {
    this.associatedUserGroupsLoaded = false;

    this._userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results != null) {
          this.associatedUserGroups = results;
          this.associatedUserGroupsLoaded = true;
          this.getUserGroups();
        }
        
        return;
      },
      () => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  getUserGroups(): void {
    this.userGroupsLoaded = false;
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.userGroupsLoaded = true;
          this.getGatewayDevices();
        }
        else {
          this.toastr.warning('No user groups found');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while retrieving all user groups');
      });
  }


  private getReadingTypesForSensorGroupId(sensorGroupId: number): SensorReadingType[] {
    var sensorReadingTypes: SensorReadingType[] = [];
    const sensorGroup = this.sensorGroups.find(x => x.id == sensorGroupId);

    if (sensorGroup == undefined || sensorGroup.sensors == undefined || sensorGroup.sensors.length < 1) {
      return sensorReadingTypes;
    }

    sensorGroup.sensors.forEach(x => {
      x.sensorReadingTypes.forEach(y => {
        if (sensorReadingTypes.find(z => z.id == y.id) == undefined) {
          var readingType: SensorReadingType = {
            id: y.id,
            type: y.type,
            checked: false
          }
          sensorReadingTypes.push(readingType);
        }
      });
    });

    return sensorReadingTypes;
  }


  getSensorGroups(): void {
    this.sensorGroupsLoaded = false;
    this._sensorGroupService.getSensorGroupWithSensorsAndReadingTypes().subscribe(
      results => {
        if (results != null) {
          this.sensorGroups = results;
          //this.sortSensorGroupsByName();
          this.mapSensorGroups2SensorConditions();
          this.mapSensorDataTypes2SensorEvents();

          this.events.forEach(e => {
            e.sensorConditions.forEach(s => s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID))
          });

          this.sensorGroupsLoaded = true;
        } else {
          this.toastr.warning('No Sensor Groups found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor groups.');
      });
  }


  sortSensorGroupsByName(): void {
    this.sensorGroups.sort((a, b) => {
      if (b.name > a.name) {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    });
  }


  getNameForSensorGroupID(sensorGroupId: number): string {
    const sensorGroup = this.sensorGroups.find(x => x.id === sensorGroupId);
    return sensorGroup != null ? sensorGroup.name : '';
  }


  private getNameForSensorReadingTypeID(sensorGroupId: number, readingTypeId: number): string {
    const readingType = this.getReadingTypesForSensorGroupId(sensorGroupId).find(x => x.id == readingTypeId);
    return readingType != null ? readingType.type : '';
  }


  mapSensorGroups2SensorConditions(): void {
    this.events.forEach(e => {
      e.sensorConditions.forEach(s => s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID));
    });
  }


  mapSensorDataTypes2SensorEvents(): void {
    this.events.forEach(e => {
      e.sensorConditions.forEach(s => s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id));
    });
  }


  addEvent(): void {
    this.event = this.newEvent();
    this.setEventType(this.event.eventType.toString());
    this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    this.event.userName = this.username;
    this.auth.mapUserAccessForEvent(this.event, this.globalSwitch, this.username, this.userGroups);
    const params: EditEventsAndActionsModalParams = {
      event: this.event,
      eventTitle: 'New Trigger',
      events: this.events,
      changeLogNotes: '',
      username: this.username,
      selectedUserGroup: this.selectedUserGroup,
      preEditObjectAsJSONString: this.preEditObjectAsJSONString,
      associatedUserGroups: this.associatedUserGroups,
      evaluateTimes: this.evaluateTimes,
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups,
      //TimingSingleDDOptions: this.TimingSingleDDOptions,
      IrrDestinationDDOptions: this.IrrDestinationDDOptions,
      CamGatewayDDOptions: this.CamGatewayDDOptions,
      IrrZoneDDOptions: this.IrrZoneDDOptions,
      globalWaterSchedules: this.globalWaterSchedules,
      buttonValue: 'Create',
      devices: this.devices,
      stopEventAction: this.stopEventAction.bind(this),
      userGroups: this.userGroups,
      actionQueues: this.actionQueues
    };
    this.modalService.show(EditEventsAndActionsModalComponent, { ...this.config, initialState: params });
  }


  onDeleteConfirm(event: Event): void {
    this.confirmationService.confirm({
      message: 'Delete the Event named \'' + event.name + '\'?',
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => {
        this._triggerService.deleteEvent(this.eventMapper.mapEvent2EventInfo(event), this.username).subscribe(
          results => {
            if (results != null) {
              this.createMessageData(MessageActionType.Deleted, event, '');
              this.toastr.success('Success');
            } else {
              this.toastr.warning('No trigger found.');
            }
            return;
          },
          () => {
            this.toastr.error('Error occurred while deleting trigger. Reload page');
          });
      },
      reject: () => {
      }
    });
  }


  copyEvent(event: Event): void {
    let copyIndex: number;
    for (copyIndex = 1; copyIndex < 500; copyIndex++) {
      const results = this.events.findIndex(t => t.name === event.name + ' (copy ' + copyIndex + ')');
      if (results === -1) {
        break;
      }
    }

    const newEvent = Object.assign({}, event);
    newEvent.id = 0;
    newEvent.isDisabled = true;
    newEvent.isDisabledColor = this.getEnabledColor(newEvent.isDisabled, newEvent.ignoreUntilDate);
    newEvent.name = event.name + ' (copy ' + copyIndex + ')';

    newEvent.sensorConditions.forEach(s => s.id = 0);
    newEvent.timerConditions.forEach(t => t.id = 0);
    newEvent.eventActions.forEach(a => a.id = 0);

    this._triggerService.createEvent(this.eventMapper.mapEvent2EventInfo(newEvent)).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Successfully copied ' + event.name);
        } else {
          this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while copying trigger.');
      });
  }


  editEvent(event: Event): void {
    this.changeLogNotes = '';

    if (event.userGroupID == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === event.userGroupID);
    }

    this.updateDeviceAndCameraNamesInEventActions(event);

    this.event = this.cloneEvent(event);
    this.preEditObjectAsJSONString = JSON.stringify(this.event);

    const params: EditEventsAndActionsModalParams = {
      event: this.event,
      eventTitle: event.name,
      events: this.events,
      changeLogNotes: this.changeLogNotes,
      username: this.username,
      selectedUserGroup: this.selectedUserGroup,
      preEditObjectAsJSONString: this.preEditObjectAsJSONString,
      associatedUserGroups: this.associatedUserGroups,
      evaluateTimes: this.evaluateTimes,
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups,
      //TimingSingleDDOptions: this.TimingSingleDDOptions,
      IrrDestinationDDOptions: this.IrrDestinationDDOptions,
      CamGatewayDDOptions: this.CamGatewayDDOptions,
      IrrZoneDDOptions: this.IrrZoneDDOptions,
      globalWaterSchedules: this.globalWaterSchedules,
      buttonValue: 'Update',
      devices: this.devices,
      stopEventAction: this.stopEventAction.bind(this),
      userGroups: this.userGroups,
      actionQueues: this.actionQueues
    };
    this.modalService.show(EditEventsAndActionsModalComponent, { ...this.config, initialState: params})
      .onHide
      .subscribe({ next: () => this.checkEventWindowState() });
  }


  cloneEvent(event: Event): Event {
    const newEvent: Event = JSON.parse(JSON.stringify(event));
    newEvent.createdDate = event.createdDate;
    newEvent.modifiedDate = event.modifiedDate;
    return newEvent;
  }


  updateDeviceAndCameraNamesInEventActions(event: Event): void {
    if (event.eventActions == null || event.eventActions.length < 1) {
      event.eventActions = [this.newEventAction()];
      return;
    }
    event.eventActions.forEach(a => {
      a.deviceName = this.getDeviceNameFromSerial(a.deviceSerial);
      a.cameraName = this.getCameraNameFromSerialAndCameraId(a.deviceSerial, a.cameraId);
    });
  }


  updateEventActionSchedules(event: Event): void {
    event.eventActions.forEach(a => {
      if (a.type === ActionType.Irrigation) {
        if (a.scheduleID !== undefined && a.scheduleID > -1) {
          a.schedule = this.globalWaterSchedules.find(g => g.id === a.scheduleID);
          a.scheduleName = (a.schedule !== undefined) ? a.schedule.name : '';
        }
        else if (a.scheduleName !== undefined) {
          a.schedule = this.globalWaterSchedules.find(g => g.name.toLowerCase() === a.scheduleName.toLowerCase());
          a.scheduleID = (a.schedule !== undefined) ? a.schedule.id : -1;

          if (a.scheduleID === -1) {
            console.log('Note: EventID: ' + event.id + ', name: ' + event.name + ' is linked to a schedule (' + a.scheduleName + ') that does not exist. Please fix');
          }
        }
      }
      else if (a.type === ActionType.FloodIrrigation) {
        if (a.fillScheduleId !== undefined && a.fillScheduleId > -1) {
          a.fillSchedule = this.globalWaterSchedules.find(g => g.id === a.fillScheduleId);
          a.fillScheduleName = (a.fillSchedule !== undefined) ? a.fillSchedule.name : '';
        }
        else if (a.fillScheduleName !== undefined) {
          a.fillSchedule = this.globalWaterSchedules.find(g => g.name.toLowerCase() === a.fillScheduleName.toLowerCase());
          a.fillScheduleId = (a.fillSchedule !== undefined) ? a.fillSchedule.id : -1;

          if (a.fillScheduleId === -1) {
            console.log('Note: EventID: ' + event.id + ', name: ' + event.name + ' is linked to a schedule (' + a.fillScheduleName + ') that does not exist. Please fix');
          }
        }

        if (a.drainScheduleId !== undefined && a.drainScheduleId > -1) {
          a.drainSchedule = this.globalWaterSchedules.find(g => g.id === a.drainScheduleId);
          a.drainScheduleName = (a.drainSchedule !== undefined) ? a.drainSchedule.name : '';
        }
        else if (a.drainScheduleName !== undefined) {
          a.drainSchedule = this.globalWaterSchedules.find(g => g.name.toLowerCase() === a.drainScheduleName.toLowerCase());
          a.drainScheduleId = (a.drainSchedule !== undefined) ? a.drainSchedule.id : -1;

          if (a.drainScheduleId === -1) {
            console.log('Note: EventID: ' + event.id + ', name: ' + event.name + ' is linked to a schedule (' + a.drainScheduleName + ') that does not exist. Please fix');
          }
        }
      }

    });
  }


  getDeviceNameFromSerial(serial: string): string {
    const device = this.devices.find(d => d.serialNumber === serial);

    if (device === null || device === undefined || device.name === '') {
      return 'unknown';
    }
    return device.name;
  }


  getCameraNameFromSerialAndCameraId(serial: string, cameraId: number): string {
    const device = this.devices.find(d => d.serialNumber === serial);

    if (device != undefined) {
      var camera = device.deviceCameras.find(x => x.id == cameraId);
      return (camera != undefined) ? camera.name : "";
    }
    return "";
  }


  getOwnerName(event: Event): string {
    if (event.userGroupID == null) {
      return event.userName;
    } else {
      return event.userGroup;
    }
  }


  evaluateEvent(event: Event, showModal: boolean, ignoreDisabled: boolean, showModalWait: boolean): void {
    if (ignoreDisabled == true && event.isDisabled) return;

    this.event = event;
    var modalType = (event.eventType === EventType.TimerBased) ? 'timer' : 'sensor';
    var waitMessage = showModalWait ? 'Evaluating event conditions ...' : '';

    this._triggerService.evaluateEvent(event.id, waitMessage).subscribe(
      results => {
        if (results != null) {
          this.taskResults = results;
          this.conditionResults = this.buildDetailsForConditionResults(event, this.taskResults.conditionResults);
          event.currentTaskResults = this.taskResults;

          //this.toastr.success('Evaluation completed');
          if (this.conditionResults != null) {
            if (modalType === 'timer') {
              const params: EvaluateTimerEventResultsModalParams = {
                event: this.event,
                conditionResults: this.conditionResults,
                taskResults: this.taskResults
              };
              if (showModal) {
                this.modalService.show(EvaluateTimerEventResultsModalComponent,
                  { ...this.evaluateInvokeConfig, initialState: params });
              }
            }
            else {
              const params: SensorConditionsEvalModalParams = {
                event: this.event,
                conditionResults: this.conditionResults,
                taskResults: this.taskResults
              };
              if (showModal) {
                this.modalService.show(SensorConditionsEvalModalComponent,
                  { ...this.evaluateInvokeConfig, initialState: params });
              }
            }
          }
        } else {
          this.toastr.warning('Error occurred while Evaluating the trigger.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while Evaluating the trigger.');
      }
    );
  }

  buildDetailsForConditionResults(event: Event, conditionResults: ConditionResult[]): ConditionResult[] {
    if (conditionResults == null) {
      return conditionResults;
    }

    event.evaluateDaysOfWeekUser = this.buildDaysOfWeekBoolUserView(event.evaluateDaysOfWeek);
    event.evaluateActiveState = event.isDisabled ? 'disabled' : this.checkEventTimeWindow(event);
    event.evaluateActiveColor = this.getEventWindowActiveColor(event.evaluateActiveState);

    conditionResults.forEach(c => {
      if (event.eventType === EventType.SensorBased) {
        const sensorCondition = event.sensorConditions.find(sEvent => sEvent.eventID === event.id && sEvent.id === c.id);
        if (sensorCondition != null && sensorCondition.sensorGroup != null) {
          c.sensorGroup = sensorCondition.sensorGroup.name;
          c.numberOfSamples = sensorCondition.numberOfSamples;
          c.lastXMinutes = sensorCondition.lastXMinutes;
          c.analysisType = sensorCondition.analysisType;
          c.condition = sensorCondition.sensorReadingType.type + ' ' + sensorCondition.comparator + ' ' + sensorCondition.threshold;
          c.logicOperator = sensorCondition.logicOperator;
          c.order = sensorCondition.order;

          c.stringValue = this.cleanConditionDetailsText(c.stringValue);
          c.detailsSummary = this.cleanConditionDetailsText(c.detailsSummary);
          c.matchedReadingsDetails = this.cleanConditionDetailsText(c.matchedReadingsDetails);
          c.allReadingsDetails = this.cleanConditionDetailsText(c.allReadingsDetails);
        }
      }

      if (event.eventType === EventType.TimerBased) {
        const timerCondition = event.timerConditions.find(tEvent => tEvent.eventID === event.id && tEvent.id === c.id);
        if (timerCondition != null) {
          c.timerType = timerCondition.type;
          c.timingSingle = timerCondition.timingSingle;
          c.timingRepeat = timerCondition.timingRepeat;
          c.daysOfWeek = this.buildDaysOfWeekBoolUserView(timerCondition.daysOfWeek);
          c.logicOperator = timerCondition.logicOperator;
          c.order = timerCondition.order;
        }
      }
    });

    this.eventMapper.sortConditionResultsByOrder(conditionResults);
    return conditionResults;
  }


  // The API builds the evaluation details as html-ish text. The modal renders it with
  // white-space: pre-wrap, so the <br> markers have to come out, and the comparators are
  // shown as symbols rather than their enum names.
  private cleanConditionDetailsText(details: string): string {
    if (details == undefined || details == '') {
      return details;
    }

    return details.replace(/<br>/g, '')
                  .replace(/ lt /g, ' < ')
                  .replace(/ lte /g, ' <= ')
                  .replace(/ gt /g, ' > ')
                  .replace(/ gte /g, ' >= ')
                  .replace(/ eq /g, ' = ')
                  .replace(/ neq /g, ' != ');
  }


  buildDaysOfWeekBoolUserView(daysOfWeek: boolean[]): string {
    let result = '';

    result += daysOfWeek[0] ? 'Su ' : '';
    result += daysOfWeek[1] ? 'M ' : '';
    result += daysOfWeek[2] ? 'Tu ' : '';
    result += daysOfWeek[3] ? 'W ' : '';
    result += daysOfWeek[4] ? 'Th ' : '';
    result += daysOfWeek[5] ? 'F ' : '';
    result += daysOfWeek[6] ? 'Sa ' : '';

    return result;
  }


  invokeEvent(event: Event): void {
    this.event = event;

    this.confirmationService.confirm({
      message: 'Pressing \'Yes\' will trigger all actions associated with this Event. <br/><br/>Event:  ' + event.name + '<br/><br/>Continue?',
      header: 'Manual Trigger Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => {
        this.invokeEventActions(event);
      },
      reject: () => {
      }
    });
  }


  invokeEvents(events: Event[]): void {
    var listOfEventNames = events.map(x => x.name).join(', ');

    this.confirmationService.confirm({
      message: 'Pressing \'Yes\' will trigger all actions associated with the select group of Events. <br/><br/>Events:  ' + listOfEventNames + '<br/><br/>Continue?',
      header: 'Manual Trigger Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => {
        events.forEach(x => {
          this.invokeEventActions(x);
          x.overallActionStatus = { state: 1, duration: 0 };
        });
      },
      reject: () => {
      }
    });
  }


  invokeEventActions(event: Event) {
    this._triggerService.invokeEventActions(event.id).subscribe(
      results => {
        if (results != null) {
          this.taskResults = results;
          this.createMessageData(MessageActionType.Edited, event, 'Manually Triggered');
          this.actionResults = this.buildDetailsForActionResults(event, this.taskResults.actionResults);
          this.toastr.success('Event actions triggered successfully');
          
          event.overallActionStatus = { state: 1, duration: 0 };
          this.startTimer4RefreshAfterManualTrigger(event);

          if (this.actionResults != null) {
            const params: InvokeResultsModalParams = {
              event,
              actionResults: this.actionResults
            };
            this.modalService.show(InvokeResultsModalComponent, { initialState: params, ...this.evaluateInvokeConfig });
          }
        } else {
          this.toastr.warning('Error occurred while manually triggering action(s) for event: ' + event.name);
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while manually triggering action(s) for event: ' + event.name);
      }
    );
  }


  buildDetailsForActionResults(event: Event, actionResults: ActionResult[]): ActionResult[] {
    if (actionResults != null) {
      actionResults.forEach(a => {
        const eventAction = event.eventActions.find(action => action.eventID === event.id && action.id === a.id);
        if (eventAction != null) {
          a.type = eventAction.type;
          a.details = this.buildActionDetailsForEventAction(eventAction);
        }
      });

      return actionResults;
    }
  }


  buildActionDetailsForEventAction(eventAction: EventAction): string {
    switch (eventAction.type) {
    case ActionType.Email:
      return eventAction.destination;
    case ActionType.Url:
      return eventAction.destination;
    case ActionType.AppMessage:
      return eventAction.destination;
    case ActionType.Task:
      return eventAction.deviceSerial + ' @ ' + eventAction.taskAction;
    case ActionType.Irrigation:
      return eventAction.deviceSerial + ' @  irrigate zone=' + eventAction.zone + ' schedule=' + eventAction.scheduleName;
    default:
      return eventAction.destination;
    }
  }


  openLogs(event: Event): void {
    const params: LogsModalParams = {
      events: [event],
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups
    };
    this.modalService.show(LogsModalComponent, { ...this.logConfig, initialState: params });
  }


  openEventLogsForSelectedEvents() {
    const selectedEvents = this.eventsDisplayed.filter(x => x.checked);

    if (selectedEvents.length < 1) {
      this.toastr.warning('No events selected for log viewing');
      return;
    }

    const params: LogsModalParams = {
      events: selectedEvents,
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups
    };
    this.modalService.show(LogsModalComponent, { ...this.logConfig, initialState: params });
  }


  public checkEventTimeWindow(event: Event): string {
    const m = moment();
    const currentMinute = (m.hour()*60) + m.minute();
    const dayOfWeek = moment().weekday();
    const startMinutes = (moment(event.evaluateStartTime, 'HH:mm').hour()*60) + moment(event.evaluateStartTime, 'HH:mm').minute();
    const endMinutes = (moment(event.evaluateEndTime, 'HH:mm').hour()*60) + moment(event.evaluateEndTime, 'HH:mm').minute();

    if (event.evaluateDaysOfWeek[dayOfWeek] === true) {      // Window starts on current day
      if (startMinutes < endMinutes) {                      // entire window during the day
        if (currentMinute >= startMinutes && currentMinute <= endMinutes ) {
          return 'active';
        }
      } else if (startMinutes > endMinutes) {                 // Window includes midnight
        if (currentMinute >= startMinutes) {                // This covers part before midnight
          return 'active';
        }
      }
    }
    // Window started day before
    if (event.evaluateDaysOfWeek[(dayOfWeek-1 < 0) ? 6 : dayOfWeek-1] === true) {
      if (startMinutes > endMinutes && currentMinute <= endMinutes) {
        return 'active';
      }
    }

    return 'inactive';
  }


  public getEnabledColor(isDisabled: boolean, ignoreUntilDate: Date): string {
    var ignore: boolean = moment(ignoreUntilDate) > moment();

    if (!isDisabled && !ignore) {
      return '#a9d18e';   // green
    } else if (isDisabled && !ignore) {
      return 'white';
    }
    else if (ignore) {
      return 'orange';
    }
  }


  public getEventWindowActiveColor(state: string): string {
    if (state === 'active') {
      return '#a9d18e';  // green
    } else if (state === 'inactive') {
      return '#f6f728';  // yellow
    } else {
      return 'clear';
    }
  }


  startTimer4RecallLastSearchFilter(): void {
    this.searchRecallTimer = setTimeout(() => {
      // A link (e.g. from the home page search) can pass the text to search with ?search=
      const linkedSearch = this.route.snapshot.queryParamMap.get('search');
      this.eventSearchText = linkedSearch ?? localStorage.getItem('TriggerSearchBox');
      this.searchEventsTextChanged();
    },500);
  }


  startTimer4RefreshAfterManualTrigger(event: Event): void {
    this.searchRecallTimer = setTimeout(() => {
      this.refreshActionStatusForEvent(event);
    },14000);
  }


  clearSearchFilter(): void {
    this.eventSearchText = '';
    this.searchEventsTextChanged();
  }


  startTimer4EventUpdateDisplay(): void {
    if (this.eventUpdateDisplayTimer) {
      clearInterval(this.eventUpdateDisplayTimer);
    }
    this.eventUpdateDisplayTimer = setInterval(() => {
      this.updatedEventToDisplay = '';
      clearInterval(this.eventUpdateDisplayTimer);
    }, 120000);
  }


  searchEventsTextChanged() {
    localStorage.setItem('TriggerSearchBox', this.eventSearchText);

    var searchResults: Event[] = [];
    const searchLimit = 100;
    let checkedCount = 0;

    this.events.forEach(x => { x.checked = false; });
    
    if (this.eventSearchText == undefined || this.eventSearchText == '' || this.eventSearchText == 'null') {
      searchResults = [];
    }
    else if (this.eventSearchText == '*') {
      searchResults = this.events;
    }
    else {
      this.eventSearchText = this.eventSearchText.toLowerCase();
      searchResults = this.events.filter(s => ((s.name != undefined ? s.name.toLowerCase().includes(this.eventSearchText) : false) ||
                                               (s.description != undefined ? s.description.toLowerCase().includes(this.eventSearchText) : false)) &&
                                              s.userFeatureAccess['ViewListing']);
    }

    const displayCount = (searchResults.length > checkedCount + searchLimit) ? checkedCount + searchLimit : searchResults.length;

    searchResults.sort((a, b) => {
      if (b.name > a.name) {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    }); 

    this.eventsDisplayed = searchResults.slice(0, displayCount);

    var eventIds = this.eventsDisplayed.map(x => x.id);
    this.getEventTooltipHistory(eventIds);

    this.refreshTableColorsValuesActionsTriggers();
  }


  refreshTableColorsValuesActionsTriggers() {
    this.getLastEventLogForEachEvent(this.eventsDisplayed);         // For displaying the last eventLog and log button background color

    this.eventsDisplayed.forEach(event => {
      this.refreshActionStatusForEvent(event);                      // For displaying the current action status color
      this.loadChartDataForEventActions(event);                     // For displaying the mini chart icon in the table

      if (event.isDisabled == false) {
        this.evaluateEvent(event, false, true, false);              // For displaying the current event condition status
      }
    });

    this.eventCheckedChanged(); 

    this.getActionQueues(true);
  }


  getEventTooltipHistory(eventIds: number[]) {
    this._triggerService.getEventTooltipHistory(eventIds).subscribe(
      results => {
        if (results != null) {
          this.eventTooltipHistory = results;
          this.mapTooltipsToEventItems();
          this.mapColorCodeToEventItems();
        } 
      },
      () => {
        this.toastr.error('Error occurred while retrieving tooltip history');
      });
  }


  mapTooltipsToEventItems() {
    this.eventsDisplayed.forEach(x => {
      x.tooltips['Threshold'] = this.getTooltipForEventItem(x.id, 'Threshold');
      x.tooltips['Enable'] = this.getTooltipForEventItem(x.id, 'Enable');
      x.tooltips['Triggered'] = this.getTooltipForEventItem(x.id, 'Triggered');
    });
  }


  mapColorCodeToEventItems() {
    this.eventsDisplayed.forEach(x => {
      x.itemColor['Threshold'] = this.getColorCodeForEventItem(x.id, 'Threshold');
      x.itemColor['Enable'] = this.getColorCodeForEventItem(x.id, 'Enable');
      x.itemColor['Triggered'] = this.getColorCodeForEventItem(x.id, 'Triggered');
    });
  }


  getTooltipForEventItem(eventId: number, eventItem: string): string {
    var tooltipHistory = this.eventTooltipHistory.filter(x => x.eventId == eventId && x.eventItem == eventItem);
    
    var results = "";

    var event = this.events.find(x => x.id == eventId);
    if (event != undefined && event.ignoreUntilDate != undefined && moment(event.ignoreUntilDate) > moment()) {
      results += "Event ignored until " + moment(event.ignoreUntilDate).format("YYYY-MM-DD h:mm A") + "\n\n";
    }

    if (tooltipHistory.length == 0) {
      results += "no recent info";
      return results;
    }
    tooltipHistory = (tooltipHistory.length == 1) ? tooltipHistory.slice(0, 1) : tooltipHistory.slice(0, 2);

    var count = 0;

    tooltipHistory.forEach(x => {
      count++;

      if (count > 1) {
        results += "\n------------------------------\n";
      }
      if (count)

      results += "user: " + x.userName.split('@')[0] + "\n";
      results += "date: " + moment.utc(x.changeDate).local().format("YYYY-MM-DD h:mm:ss a") + "\n";
      results += "details: " + x.details ;

      if (x.changeLog != undefined && x.changeLog != 'null' && x.changeLog != '') {
        results += "\n" + "notes: " + x.changeLog;
      }
    });
   
    return results;
  }


  getColorCodeForEventItem(eventId: number, eventItem: string): string {
    var tooltipHistory = this.eventTooltipHistory.filter(x => x.eventId == eventId && x.eventItem == eventItem);
    
    if (tooltipHistory.length == 0) return "no info";
    tooltipHistory = tooltipHistory.slice(0, 1);

    var diffHours = moment().diff(moment.utc(tooltipHistory[0].changeDate).local(),'hours');

    if (diffHours <= 48) {
      return "#c8102e";
    }
    else {
      return "black";
    }
  }


  getConditionForTable(event: Event): string {
    if (event.eventType === EventType.SensorBased) {
      if (event.sensorConditions == null || event.sensorConditions.length === 0) {
        return '-';
      } else if (event.sensorConditions.length === 1) {
        return event.sensorConditions[0].sensorGroup.name + ' | ' + event.sensorConditions[0].sensorReadingType.type + ' ' + event.sensorConditions[0].comparator + ' ' + event.sensorConditions[0].threshold;
      } else {
        return event.sensorConditions[0].sensorGroup.name + ' | ' + event.sensorConditions[0].sensorReadingType.type + ' ' + event.sensorConditions[0].comparator + ' ' + event.sensorConditions[0].threshold + ' (+' + (event.sensorConditions.length-1).toString() + ')';
      }
    }

    else if (event.eventType === EventType.TimerBased) {
      if (event.timerConditions == null || event.timerConditions.length === 0) {
        return '-';
      } else if (event.timerConditions.length === 1) {
        if (event.timerConditions[0].type === 'Single') {
          return event.timerConditions[0].timingSingle + ' (' + this.buildDaysOfWeekBoolUserView(event.timerConditions[0].daysOfWeek) + ')';
        } else {
          return event.timerConditions[0].timingRepeat + ' (' + this.buildDaysOfWeekBoolUserView(event.timerConditions[0].daysOfWeek) + ')';
        }
      } else {
        if (event.timerConditions[0].type === 'Single') {
          return event.timerConditions[0].timingSingle + ' (' + this.buildDaysOfWeekBoolUserView(event.timerConditions[0].daysOfWeek) + ')' + ' (+' + (event.timerConditions.length-1).toString() + ')';
        } else {
          return event.timerConditions[0].timingRepeat + ' (' + this.buildDaysOfWeekBoolUserView(event.timerConditions[0].daysOfWeek) + ')' + ' (+' + (event.timerConditions.length-1).toString() + ')';
        }
      }
    }

    else if (event.eventType === EventType.ModelBased) {
      if (event.modelConditions == null || event.modelConditions.length === 0) {
        return '-';
      } else {
        return 'multiple(' + event.modelConditions.length.toString() + ')';
      }
    }

    else if (event.eventType === EventType.MotionBased) {
      if (event.motionConditions == null || event.motionConditions.length === 0) {
        return '-';
      } else if (event.motionConditions.length === 1) {
        return `${event.motionConditions[0].detectionArea} (sen=${event.motionConditions[0].sensitivity}, sleep=${event.motionConditions[0].postMotionSleepTime})`;
      } else {
        return 'multiple motions (' + event.motionConditions.length.toString() + ')';
      }
    }
  }


  getActionForTable(event: Event): string {
    if (event.eventActions == null) {
      return '-';
    }
    if (event.eventActions.length === 1) {
      return event.eventActions[0].type;
    } else {
      return 'multiple(' + event.eventActions.length.toString() + ')';
    }
  }


  // True when the event's single action is a flood irrigation -- i.e. the Actions
  // column reads "FloodIrrigation" -- so that cell can link straight to the manual
  // flood irrigation modal (the same entry point as the /manualflood/<id> route).
  isSingleFloodIrrigationAction(event: Event): boolean {
    return event.eventActions != null
      && event.eventActions.length === 1
      && event.eventActions[0].type === ActionType.FloodIrrigation;
  }


  startTimerEventWindowCheck(): void {
    this.checkEventWindowState();
    setInterval(() => {
      this.checkEventWindowState();
    },30000);
  }


  checkEventWindowState(): void {
    if (this.events == null) {
      return;
    }
    this.events.forEach(e => {
      e.evaluateActiveState = e.isDisabled ? 'disabled' : this.checkEventTimeWindow(e);
      e.evaluateActiveColor = this.getEventWindowActiveColor(e.evaluateActiveState);
      e.isDisabledColor = this.getEnabledColor(e.isDisabled, e.ignoreUntilDate);
    });
  }


  changeEventEnableState(event: Event, isDisabled: boolean): void {
    event.userName = this.username;
    if (event.userGroupID == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === event.userGroupID);
      event.userGroupID = this.selectedUserGroup.id;
      event.userGroup = this.selectedUserGroup.name;
    }

    event.isDisabled = isDisabled;
    event.isDisabledColor = this.getEnabledColor(event.isDisabled, event.ignoreUntilDate);

    const eventInfo = this.eventMapper.mapEvent2EventInfo(event);
    eventInfo.modifiedDate = moment().utc().toDate();

    this.createMessageData(MessageActionType.Edited, event, 'Enabled (' + (event.isDisabled).toString() + ' -> ' + (!event.isDisabled).toString() + ')');

    this._triggerService.updateEvent(eventInfo, '', this.username).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Success');
          this.checkEventWindowState();
          this.checkIfActionQueueItemsNeedCanceling(event);
        }
        else {
          this.toastr.warning('Error updating event');
          event.isDisabled = !event.isDisabled;
          event.isDisabledColor = this.getEnabledColor(event.isDisabled, event.ignoreUntilDate);
          this.checkEventWindowState();
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while updating event');
        event.isDisabled = !event.isDisabled;
        event.isDisabledColor = this.getEnabledColor(event.isDisabled, event.ignoreUntilDate);
        this.checkEventWindowState();
      });
  }


  checkIfActionQueueItemsNeedCanceling(event: Event): void {
    if (!event.isDisabled) return;
    // event.actionQueue is undefined for events not associated with an action queue
    // (it is set via .find() in the queue-position logic). Such an event has nothing
    // waiting to cancel, so guard before dereferencing queuedWaiting.
    if (event.actionQueue == undefined || event.actionQueue.queuedWaiting == undefined || event.actionQueue.queuedWaiting.length < 1) return;

    this.confirmationService.confirm({
      message: 'Disabled Event named \'' + event.name + '\' has pending action queue items. Do you want to cancel them?',
      header: 'Event Action Queue Cancellation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => {
        var queueWaitingForEvent = event.actionQueue.queuedWaiting.filter(x => x.eventId == event.id);
        queueWaitingForEvent.forEach(queueItem => {
          this._actionQueueService.cancelQueuedItem(queueItem.id, "").subscribe(
            results => {
              if (results != null) {
                if (queueWaitingForEvent[queueWaitingForEvent.length - 1].id == queueItem.id) {
                  this.toastr.success('Pending action queue items for this Event have been canceled.');
                  this.createMessageData(MessageActionType.Edited, event, 'Canceled pending action queue items due to event being disabled.');
                  this.getActionQueues(true);
                }
              }
            },
            () => {
              this.toastr.error('Error occurred while canceling pending action queue item for this event.');
            });
        });

        
      },
      reject: () => {
      }
    });
  }


  setupQuickEditThreshold(event: Event): void {
    if (event.eventType === EventType.SensorBased && event.sensorConditions != null && event.sensorConditions.length === 1) {
      this.event = event;
      const params: EditEventConditionThresholdParams = {
        event,
        username: this.username,
        quickEditThreshold: event.sensorConditions[0].threshold,
        associatedUserGroups: this.associatedUserGroups
      };
      this.modalService.show(EditEventConditionThresholdComponent, { animated: false, backdrop: false, ignoreBackdropClick: false, initialState: params } );
    } else {
      this.toastr.warning('Unable to use quick edit for this event');
    }
  }


  createMessageData(msgActionType: MessageActionType, event: Event, msgNote: string): void {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.EventAndActions,
      actionType: msgActionType,
      appComponentItem: event.name,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: event.userGroupID
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  openScanModal(template: TemplateRef<any>): void {
    this.scannedSensor = '';
    this.scanModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  public createUUID(uuid): string {
    uuid = uuid.split(':').join('');
    const formattedUUID = [];
    const maxLength = 36;
    let uuidLengthTracker = uuid.length - 1;
    for (let i = 0; i <= maxLength - 1; i++) {
      if (i == 12 || i == 17 || i == 22 || i == 27) {
        formattedUUID.unshift('-');
      } else if (uuidLengthTracker >= 0) {
        formattedUUID.unshift(uuid.charAt(uuidLengthTracker));
        uuidLengthTracker--;
      } else {
        formattedUUID.unshift('0');
      }
    }
    return formattedUUID.join('');
  }


  public scanComplete(sensor: string): void {
    this.scannedSensor = sensor;
  }


  public camerasFound(cameras: MediaDeviceInfo[]) {
    this.availableDevices = cameras;
    if (this.availableDevices.length > 0) {
      this.onDeviceSelectChange(this.availableDevices[this.availableDevices.length-1].deviceId);
    }
  }


  onDeviceSelectChange(selectedValue: string) {
    this.selectedDevice = this.availableDevices.find(x => x.deviceId == selectedValue);
  }


  public locateEventsAssociatedWithScannedSensor(): void {
    if (this.scannedSensor == undefined || this.scannedSensor == '') {
      this.toastr.warning('Please scan a sensor');
      return;
    }
    this.scanModalRef.hide();
    this.getSensorGroups();
  }


  parseSensorUUID(): string {
    const matchExp = '[a-zA-Z][-]';
    const matchesPattern = this.scannedSensor.substring(0, 2).search(matchExp) >= 0;
    if (matchesPattern) {
      return this.scannedSensor.substring(2, this.scannedSensor.length).toLowerCase().replace(/:/g, '');
    }
    return this.scannedSensor.toLowerCase().replace(/:/g, '');
  }


  addNotes2ChangeLog(editSource: string) {
    this.changeLogNotes = '';
    var userGroupId = 0;

    if (editSource == MessageNoteCategory.EventEditGroup) {
      userGroupId = this.eventsDisplayed.filter(x => x.checked)[0].userGroupID;
    }
    else if (editSource == MessageNoteCategory.EventTrigger) {
      userGroupId = this.event.userGroupID;
    }

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: userGroupId,
      category: editSource
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }


  toggleCheckedEventsDisplayed() {
    if (this.eventsDisplayed == undefined || this.eventsDisplayed.length < 1) {
      return;
    }
    var newState = !this.eventsDisplayed[0].checked;
    this.eventsDisplayed.forEach(event => {
      event.checked = newState;
    });

    this.eventCheckedChanged();
  }


  showSelectedEvents() {
    this.eventsDisplayed = this.events.filter(x => x.checked);
  }


  eventCheckedChanged() {
    this.eventsCheckedCount = this.eventsDisplayed.filter(x => x.checked).length;
  }


  openGroupEventActions(template: TemplateRef<any>) {
    this.groupChangeAction = '';
    this.groupChangeModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  executeGroupChange() {
    this.groupChangeModalRef.hide();

    var eventsChecked = this.eventsDisplayed.filter(x => x.checked);

    switch (this.groupChangeAction) {
      case "ManualTrigger":
        this.invokeEvents(eventsChecked);
        break;

      case "DisableAll":
        eventsChecked.forEach(x => this.changeEventEnableState(x, true));
        break;

      case "EnableAll":
        eventsChecked.forEach(x => this.changeEventEnableState(x, false));
        break;

      case "Pause":
        var ignoreUntilDate = moment().local().add(this.groupPauseValue, 'minutes').toDate();
        eventsChecked.forEach(event => {
          event.ignoreUntilDate = ignoreUntilDate;
        });
        this.updateEventIgnoreUntilDate(eventsChecked.map(x => x.id), ignoreUntilDate);
        break;

      case "Unpause":  
        var ignoreUntilDate = moment().local().add(-60, 'minutes').toDate();
        eventsChecked.forEach(event => {
          event.ignoreUntilDate = ignoreUntilDate;
        });
        this.groupPauseValue = 0;
        this.updateEventIgnoreUntilDate(eventsChecked.map(x => x.id), ignoreUntilDate);
        break;

      case "ChangeThresholdBy":
        eventsChecked.forEach(event => {
          if (event.eventType == EventType.SensorBased && event.sensorConditions != undefined && event.sensorConditions.length > 0) {
            var origialEvent = this.cloneEvent(event);
            var newEvent = this.cloneEvent(event);
            newEvent.sensorConditions.forEach(x => {
              x.threshold += this.groupThresholdChangeByValue;
            });
            this.updateEventConditionThresholds(newEvent, origialEvent);
          }
        });
        break;

      case "SetThresoldTo":
        eventsChecked.forEach(event => {
          if (event.eventType == EventType.SensorBased && event.sensorConditions != undefined && event.sensorConditions.length > 0) {
            var origialEvent = this.cloneEvent(event);
            var newEvent = this.cloneEvent(event);
            newEvent.sensorConditions.forEach(x => {
              x.threshold = this.groupThresholdSetToValue;
            });
            this.updateEventConditionThresholds(newEvent, origialEvent);
          }
        });
        break;

      case "SetActionQueueTo":
        this.setEventsToUseActionQueue(eventsChecked.map(x => x.id), this.groupActionQueueSetToValue.id);
        break;

      default:
        break;
    }
  }


  setEventsToUseActionQueue(eventIds: number[], actionQueueId: number) {
    if (eventIds == null || eventIds.length < 1) {
      return;
    }
    this._actionQueueService.setEventsToUseActionQueue(eventIds, actionQueueId, "Setting Event(s) Action Queue Usage").subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Success');
          eventIds.forEach(eventId => {
            var event = this.events.find(x => x.id == eventId);
            if (event != undefined) {
              event.actionQueueId = actionQueueId;
              var message = this.actionQueues.find(x => x.id == actionQueueId)?.name;
              this.createMessageData(MessageActionType.Edited, event, `Event actions queue usage set to '${message}'`);
            }
          });
          this.refreshTableColorsValuesActionsTriggers();
        } else {
          this.toastr.warning('Error updating event action queue');
        } 
        return;
      },
      err => {  
        this.toastr.error('Error occurred while updating event action queue');
      });
  }


  updateEventIgnoreUntilDate(eventIdsToUpdate: number[], ingnoreUntilDate: Date) {
    if (eventIdsToUpdate == null || eventIdsToUpdate.length < 1) {
      return;
    }

    var newEventIgnore: EventIgnore = {
      eventIds: eventIdsToUpdate,
      ignoreUntilDate: ingnoreUntilDate
    }

    this._triggerService.updateEventsIgnoreDate(newEventIgnore, "Updating Event(s)").subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Success');

          eventIdsToUpdate.forEach(eventId => {
            var event = this.events.find(x => x.id == eventId);

            if (event != undefined) {
              if (this.groupPauseValue > 0) {
                var message = `Event paused for ${this.groupPauseValue} minutes`;
                this.createMessageData(MessageActionType.Edited, event, message);
              }
              else {
                var message = `Event unpaused`;
                this.createMessageData(MessageActionType.Edited, event, message);  
              }
            }
          });
        } else {
          this.toastr.warning('Error updating event pause state');
        }
        return;
      },
      err => {

        this.toastr.error('Error occurred while updating event pause state');
      });
  }


  updateEventConditionThresholds(event: Event, originalEvent: Event) {
    event.userName = this.username;
    if (event.userGroupID == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === event.userGroupID);
      event.userGroupID = this.selectedUserGroup.id;
      event.userGroup = this.selectedUserGroup.name;
    }

    if (event.sensorConditions == null || event.sensorConditions.length < 1) {
      return;
    }

    const eventInfo = this.eventMapper.mapEvent2EventInfo(event);
    eventInfo.modifiedDate = moment().utc().toDate();

    this._triggerService.updateEvent(eventInfo, '', this.username).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Success');

          var message: string = '';
          var count = event.sensorConditions.length;
          for (var index=0; index < count; index++) {
            var x = event.sensorConditions[index];
            message += this.common.compareProperty(`SensorCond.${index+1}.threshold`, this.common.prepareProperty4String(originalEvent.sensorConditions[index].threshold), this.common.prepareProperty4String(x.threshold));
          }
          this.createMessageData(MessageActionType.Edited, event, message);

        } else {
          this.toastr.warning('Error updating event threshold');
        }
        return;
      },
      err => {

        this.toastr.error('Error occurred while updating event threshold');
      });
  }


  refreshActionStatusForEvent(event: Event) {
    if (event.eventActions != null && event.eventActions.length == 1 && event.eventActions[0].type == ActionType.Irrigation) {
      event.overallActionStatus = { state: 1, duration: 0 };      // 0: No status, 1: Waiting for status, 2: Not busy, 3: Busy, 4: unknown

      this._triggerService.refreshTriggerStatus(event.id).subscribe(
        results => {
          if (results != null) {
            event.overallActionStatus = results;
            event.overallActionStatus.duration = Math.trunc(event.overallActionStatus.duration * 10) / 10;
          }
          else {
            this.toastr.warning('Error refreshing trigger status');
            event.overallActionStatus = { state: 4, duration: 0 };
          }
          return;
        },
        () => {
          this.toastr.error('Error occurred while refreshing trigger status');
          event.overallActionStatus = { state: 4, duration: 0 };
        });
    }
    else {
      event.overallActionStatus = { state: 2, duration: 0 };
    }
  }


  stopEventAction(event: Event) {
    if (event.eventActions != null && event.eventActions.length == 1 && event.eventActions[0].type == ActionType.Irrigation) {
      event.overallActionStatus = { state: 1, duration: 0 };      // 0: No status, 1: Waiting for status, 2: Not busy, 3: Busy, 4: unknown

      var device = this.devices.find(x => x.serialNumber == event.eventActions[0].deviceSerial);
      var command = `/home/pi/kigs/scripts/irrigation/stopIrrigationForZone.sh ${event.eventActions[0].zone}`;
      var payload = '';
      var waitMessage = '';
      const task = this.buildNewTask(device.id, command, payload);

      this.deviceService.postTask(task, waitMessage, 4).subscribe(
        results => {
          try {
            var response = JSON.parse(results);  
            if (response.status === 'success') {
              this.toastr.success('Irrigation stopped successfully');
              this.refreshActionStatusForEvent(event);
              this.createMessageData(MessageActionType.Edited, event, 'Manually stopped irrigation')
            } else {
              this.toastr.warning('Error stopping irrigation: ' + results);
            }
          }
          catch (error) {
            this.toastr.warning('Unable to parse response from gateway. Response was: ' + results);
          };
        }
      );
    }
  }


  buildNewTask(deviceID: number, action: string, payload: string): Task {
    const task: Task = {
      taskID: -1,
      deviceID,
      clientID: this.common.generateUUID(),
      priority: 1,
      type: 1,
      action,
      payload,
      expireTimeSec: 87,
      taskWaitIntervalSec: 2,
      waitForResult: true
    };

    return task;
  }


  public getLastEventLogForEachEvent(events: Event[]) {
    var eventIds = events.filter(e => e.id > 0 && e.isDisabled === false).map(e => e.id);

    this._triggerService.GetLastEventLogForEachEvent(eventIds).subscribe(
      results => {
        if (results != null) {
          var eventLogInfos: EventLogInfo[] = results;
          eventLogInfos.forEach(e => {
            const eventLog: EventLog = {
              createdDate: moment.utc(e.createdDate).local().toDate(),
              conditionResults: this.common.buildCombinedConditionResults(e, this.sensorGroups, this.screenInnerWidth),
              actionResults: this.common.buildCombinedActionResults(e),
              eventLogInfoID: e.id,
              overallConditionResult: '',
              id: e.id,
              eventName: this.events.find(x => x.id == e.eventID)?.name || ''
            };
            eventLog.overallConditionResult = (e.actionResultInfos.length == 0) ? 'False' : 'True';

            const event = this.events.find(ev => ev.id == e.eventID);
            if (event != null) {
              event.lastEventLog = eventLog;
              event.lastEventLogColor = this.getFirstEventLogActionColor(event);
            }
          });

        } else {
          this.toastr.warning('No event logs found for events: ' + this.events.map(e => e.name).join(', '));
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving event logs for ' + this.events.map(e => e.name).join(', '));
      });
  }


  getFirstEventLogActionColor(event: Event): string {
    if (event.lastEventLog != null && event.lastEventLog.actionResults != null && event.lastEventLog.actionResults.length > 0) {
      const firstActionResult = event.lastEventLog.actionResults[0];
      return this.common.getEventLogColor(firstActionResult.value);
    }

    return 'transparent';
  }


  getConditionEvalValue(event: Event, modelType: string): string {
    if (event.eventType == EventType.SensorBased && event.currentTaskResults != null && event.currentTaskResults.conditionResults.length > 0) {
      event.currentTaskResults.conditionResults.forEach(x => x.numericalValue = Number(Number(x.numericalValue).toPrecision(4)));
      var values = event.currentTaskResults.conditionResults.map(c => c.numericalValue).join(', ');
      return (values.length > 10) ? values.substring(0, 8) + '...' : values;
    }

    if (event.eventType == EventType.TimerBased && event.currentTaskResults != null && event.currentTaskResults.conditionResults.length > 0) {
      var values = event.currentTaskResults.conditionResults.map(c => c.booleanValue).join(', ');
      return (values.length > 12) ? values.substring(0, 8) + '...' : values;
    }

    return '';
  }


  getEventConditionStatusColor(event: Event): string {
    if (event.currentTaskResults != null) {
      if (event.currentTaskResults.overallConditionResult === true) {
        return '#a9d18e';   // green
      }
      else {
        return 'white';
      }
    }
  }


  onColumnToggle() {
    this.saveColumnSelection();
  }

  saveColumnSelection() {
    const selectedFields = this.selectedColumns.map(col => col.field);
    localStorage.setItem('triggerTable_selectedColumns', JSON.stringify(selectedFields));
  }


  onColumnResize(event: any) {
    const colIndex = event.element.cellIndex;
    const column = this.selectedColumns[colIndex];
    
    // Only save width if column is resizable
    if (column && column.resizable !== false) {
      const newWidth = event.element.offsetWidth + 'px';
      column.width = newWidth;
      this.saveColumnWidths();
    }
  }


  saveColumnWidths() {
    const widths = {};
    this.selectedColumns.forEach(col => {
      widths[col.field] = col.width;
    });
    localStorage.setItem('triggerTable_columnWidths', JSON.stringify(widths));
  }


  loadColumnWidths() {
    const saved = localStorage.getItem('triggerTable_columnWidths');
    if (saved) {
      const widths = JSON.parse(saved);
      this.eventTableCols.forEach(col => {
        if (widths[col.field]) {
          col.width = widths[col.field];
        }
      });
    }
  }


  loadSelectedColumns() {
    const saved = localStorage.getItem('triggerTable_selectedColumns');
    if (saved) {
      const savedFields = JSON.parse(saved);
      this.selectedColumns = this.eventTableCols.filter(col => 
        savedFields.includes(col.field)
      );
    }
  }

  
  resetColumnsToDefault() {
    this.eventTableCols.forEach(col => col.width = col.defaultWidth);
    this.selectedColumns = [...this.eventTableCols];

    window.location.reload();

    this.saveColumnSelection();
    this.saveColumnWidths();
  }


  loadChartDataForEventActions(event: Event) {
    if (event.miniChartData == undefined || event.miniChartData.sensorReadings == undefined ) {
      return;
    }

    event.miniChartData = {
      sensors: [],
      sensorReadings: [],
      sensorReadingTypes: []
    };

    this._sensorService.getSensorReadingsForEvent(event.id, 24).subscribe(
      results => {
        if (results != null) {
          event.miniChartData.sensorReadings = results;
          event.miniChartData.sensorReadings.forEach((reading, index) => {
            reading.createdDate = moment.utc(reading.createdDateMS).local().toDate();
            reading.sensorName = '';
            reading.value = Number(reading.value).toPrecision(9);
          });

          event.miniChartData.sensorReadings.sort((a, b) => {
            if (b.createdDateMS > a.createdDateMS) {
              return -1;
            }
            if (b.createdDateMS < a.createdDateMS) {
              return 1;
            }
          });

        } else {
          this.toastr.warning('No readings for Event Actions: ' + this.event.name);
        }
        return;
      },
      err => {
        this.toastr.error('Error getting readings for Event Actions: ' + this.event.name);
      });
  }


  getActionQueues(forceReload: boolean): void {
    this._actionQueueService.getActionQueues('Loading Action Queues', forceReload).subscribe(
      results => {
        if (results != null) {
          this.actionQueues = [];
          this.actionQueues.push( {
            id: 0, eventIds: [], queued: [], modifiedDate: new Date(), userGroupId: undefined,
            name: 'No Action Queue', userName: '', userGroupName: '', userId: 0, userAccess: UserAccessType.Read,
            rules: '', rulesObj: new ActionQueueRules(),
            queuedWaiting: [], queuedHistory: [], queuedActive: 0, saveQueueHistoryDays: 0
          } );
          this.groupActionQueueSetToValue = this.actionQueues[0];
          this.actionQueues.push(...results);

          this.actionQueues.forEach(actionQueue => {
            this.auth.mapUserAccessForActionQueue(actionQueue, false, this.username, this.userGroups);
            actionQueue.eventIds = (actionQueue.eventIds == null) ? [] : actionQueue.eventIds;
            actionQueue.queued = (actionQueue.queued == null) ? [] : actionQueue.queued;
            actionQueue.modifiedDate = moment.utc(actionQueue.modifiedDate).local().toDate();
            actionQueue.queuedActive = actionQueue.queued.filter(a => a.status == 'active').length;

            actionQueue.queued.forEach(x => {
              var event = this.events.find(e => e.id === x.eventId);
              if (event != null) {
                x.eventName = event.name;
                x.actionType = this.common.getEventActionLabelForEvent(event);
                x.createdDate = moment.utc(x.createdDate).local().toDate();
                x.modifiedDate = moment.utc(x.modifiedDate).local().toDate();
              }
              this.common.setStatusColorForActionQueueLiveItem(x);
            });

            actionQueue.queuedWaiting = actionQueue.queued.filter(q => q.status.toLowerCase() == 'waiting');
            actionQueue.queuedHistory = actionQueue.queued.filter(q => q.status.toLowerCase() != 'waiting');

            this.common.sortActionQueueByColumn(actionQueue.queuedHistory, 'modifiedDate', false);
            this.common.sortActionQueueByColumn(actionQueue.queuedWaiting, 'orderNum', true);
            this.common.sortActionQueueByColumn(actionQueue.queued, 'createdDate', false);

            this.events.forEach(event => {
              this.assignActionQueuePositionToEvent(event);
            });
          });
        }
      },
      () => {
        this.toastr.error('Error occurred while retrieving action queues');
      });
    }
  

  assignActionQueuePositionToEvent(event: Event) {
    event.actionQueue = this.actionQueues.find(aq => aq.eventIds.includes(event.id));

    if (event.eventActions == null || event.actionQueue == null || event.actionQueue.queuedWaiting == null || event.actionQueue.queuedWaiting.length < 1) {
      event.actionQueuePosition = 0;
    }
    else {
      var posInWaitingQueue = event.actionQueue.queuedWaiting.findIndex(a => a.eventId === event.id);
      event.actionQueuePosition = (posInWaitingQueue > -1) ? posInWaitingQueue + 1 : 0;
    }
  }


  openQueuedEventActions(event: Event) {
    if (event.actionQueue == undefined || event.actionQueueId < 1 || event.actionQueue.queued == undefined) { // || event.actionQueue.queued.length < 1) {
      return;
    }

    const params: ViewActionQueueParams = {
      actionQueue: event.actionQueue,
      userGroups: this.userGroups,
      associatedUserGroups: this.associatedUserGroups,
    };

    this.modalService.show(ViewActionQueueComponent, { animated: false, backdrop: false, ignoreBackdropClick: false, initialState: params });
  }


}